import { Eye, Minus, Plus, UserCircle2 } from "lucide-react";
import { Filter, SortByRating, Tick } from "../AdminIcons";
import {
  useGetCourseCatalogAdminSideQuery,
  useGetInstructorsAssignedToACourseQuery,
  useGetInstructorsThatCanBeAssignedToACourseQuery,
  useAssignInstructorToACourseMutation,
  useRemoveInstructorFromCourseMutation,
} from "../../services/course/courseApi";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const DAY_LABELS = {
  MON: "Mon",
  TUE: "Tue",
  WED: "Wed",
  THU: "Thu",
  FRI: "Fri",
  SAT: "Sat",
  SUN: "Sun",
};

const formatTime = (time24) => {
  if (typeof time24 !== "string" || !time24.includes(":")) {
    return "";
  }

  const [hourStr, minuteStr] = time24.split(":");
  const hour24 = parseInt(hourStr, 10);

  if (Number.isNaN(hour24)) {
    return "";
  }

  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  return `${hour12}:${minuteStr || "00"} ${period}`;
};

const formatAvailability = (availabilityList) => {
  if (!Array.isArray(availabilityList) || availabilityList.length === 0) {
    return "Not specified";
  }

  const formattedSlots = availabilityList
    .filter((slot) => slot?.isActive !== false)
    .map((slot) => {
      const days = Array.isArray(slot?.days)
        ? slot.days.map((day) => DAY_LABELS[day] || day).join(", ")
        : "";
      const timeRange =
        slot?.startTime && slot?.endTime
          ? `${formatTime(slot.startTime)} to ${formatTime(slot.endTime)}`
          : "";

      return [days, timeRange].filter(Boolean).join(" - ");
    })
    .filter(Boolean);

  return formattedSlots.length > 0
    ? formattedSlots.join("; ")
    : "Not specified";
};

const Modal = ({
  selectedInstructor,
  modalAction,
  onAssign,
  onRemove,
  onClose,
  isAssigning,
  isRemoving,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-3 py-6 sm:px-4">
    <div className="absolute inset-0 bg-black/50" onClick={onClose} />

    <div className="relative w-full max-w-[429px] rounded-[20px] bg-white px-4 pb-6 pt-14 sm:px-8 sm:pb-8 sm:pt-16">
      <button
        onClick={onClose}
        className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
      >
        ✕
      </button>
      <div className="absolute left-1/2 top-5 -translate-x-1/2 -translate-y-1/2">
        <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-[#923D8C] bg-[#F3F3F3] sm:h-[86px] sm:w-[86px]">
          {selectedInstructor.imageUrl ? (
            <img
              src={selectedInstructor.imageUrl}
              alt={selectedInstructor.name}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <UserCircle2 size={40} className="text-[#6366F1]" />
          )}
        </div>
      </div>

      <div className="mt-4 text-center">
        <h3 className="break-words text-2xl leading-8 text-black sm:text-[32px] sm:leading-9">
          {selectedInstructor.name}
        </h3>
        <p className="mt-0.5 break-words text-base text-[#007AFF] sm:text-2xl">
          {selectedInstructor.email}
        </p>
        {/* <p className="mt-3 text-sm text-[#111827]">
                Skills | {selectedInstructor.specialization || "N/A"}
              </p> */}
      </div>

      <div className="mt-8 flex justify-center">
        <button
          type="button"
          onClick={modalAction === "assign" ? onAssign : onRemove}
          disabled={
            (modalAction === "assign" && isAssigning) ||
            (modalAction === "remove" && isRemoving)
          }
          className={`inline-flex items-center justify-center rounded leading-5 px-4 py-[9px] text-sm font-semibold text-white transition bg-[#923D8C] hover:bg-[#6c2c68] disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {modalAction === "assign"
            ? isAssigning
              ? "Assigning..."
              : "Assign Instructor"
            : isRemoving
              ? "Removing..."
              : "Remove Instructor"}
        </button>
      </div>
    </div>
  </div>
);

const CourseInstructorsSection = ({ courseId, courseData }) => {
  const resolvedCourseId = useMemo(() => {
    const fromParams = typeof courseId === "string" ? courseId.trim() : "";
    const courseRecord = Array.isArray(courseData?.data)
      ? courseData.data[0]
      : courseData?.data;
    const fromCourseData =
      typeof courseRecord?._id === "string" ? courseRecord._id.trim() : "";

    return fromParams || fromCourseData || "";
  }, [courseId, courseData]);

  const navigate = useNavigate();

  const { data: courseCatalogResponse } = useGetCourseCatalogAdminSideQuery({
    page: 1,
    limit: 100,
  });
  const {
    data: instructorsResponse,
    error: assignedInstructorsError,
    isError: isAssignedInstructorsError,
    refetch: refetchAssignedInstructors,
  } = useGetInstructorsAssignedToACourseQuery(resolvedCourseId, {
    skip: !resolvedCourseId,
  });
  const { data: availableResponse, refetch: refetchAvailableInstructors } =
    useGetInstructorsThatCanBeAssignedToACourseQuery(resolvedCourseId, {
      skip: !resolvedCourseId,
    });
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [modalAction, setModalAction] = useState(null); // 'assign' or 'remove'
  const [assignInstructorToACourse, { isLoading: isAssigning }] =
    useAssignInstructorToACourseMutation();
  const [removeInstructorFromCourse, { isLoading: isRemoving }] =
    useRemoveInstructorFromCourseMutation();
  const courseMap = useMemo(() => {
    const courses = Array.isArray(courseCatalogResponse?.data)
      ? courseCatalogResponse.data
      : [];
    return courses.reduce((map, course) => {
      if (course?._id) {
        map[course._id] = course?.courseTitle || "Untitled course";
      }
      return map;
    }, {});
  }, [courseCatalogResponse]);

  const openModal = (instructor, action) => {
    setSelectedInstructor(instructor);
    setModalAction(action);
  };

  const clearSelection = () => {
    setSelectedInstructor(null);
    setModalAction(null);
  };

  const refreshInstructorLists = async () => {
    await Promise.all([
      refetchAssignedInstructors(),
      refetchAvailableInstructors(),
    ]);
  };

  const handleAssign = async () => {
    if (!resolvedCourseId || !selectedInstructor?.id) {
      return;
    }
    try {
      await assignInstructorToACourse({
        courseId: resolvedCourseId,
        instructorId: selectedInstructor.userId,
      }).unwrap();
      await refreshInstructorLists();
    } catch (error) {
      console.error("Assigning instructor failed:", error);
    } finally {
      clearSelection();
    }
  };

  const handleRemove = async () => {
    const instructorId =
      selectedInstructor?.instructorId || selectedInstructor?.id;

    if (!resolvedCourseId || !instructorId) {
      return;
    }

    try {
      await removeInstructorFromCourse({
        courseId: resolvedCourseId,
        instructorId,
      }).unwrap();
      await refreshInstructorLists();
      clearSelection();
    } catch (error) {
      console.error("Failed to remove instructor:", error);
    }
  };
  const assignedInstructors = useMemo(() => {
    const raw = instructorsResponse?.data ?? instructorsResponse ?? [];
    const instructors = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : [];

    return instructors.map((ins, idx) => ({
      id: ins._id || idx * 5,
      instructorId: ins.user?.id || ins.user?._id || ins._id,
      name: `${ins.user?.firstName || ""} ${ins.user?.lastName || ""}`.trim(),
      email: ins.user?.email || "",
      specialization: ins.qualification || "",
      assignDate: ins.createdAt
        ? new Date(ins.createdAt).toLocaleDateString("en-US")
        : "-",
      imageUrl: ins.user?.profilePicture.secureUrl,
    }));
  }, [instructorsResponse]);

  const availableInstructors = useMemo(() => {
    const raw = availableResponse?.data ?? [];
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : [];

    return list.map((ins, idx) => {
      const rawPreferredCourses = ins?.coursePreferences || [];
      const preferredCourseTitles = Array.isArray(rawPreferredCourses)
        ? rawPreferredCourses.map((coursePref) => {
            if (typeof coursePref === "string") {
              return courseMap[coursePref] || coursePref;
            }
            if (coursePref?._id) {
              return (
                coursePref.courseTitle ||
                courseMap[coursePref._id] ||
                coursePref._id
              );
            }
            return String(coursePref);
          })
        : [];

      return {
        id: ins._id || idx * 231,
        name: `${ins.user?.firstName || ""} ${ins.user?.lastName || ""}`.trim(),
        userId: ins.user?.id,
        email: ins.user?.email,
        specialization: ins.qualification || "",
        preferredCourseTitles,
        imageUrl: ins.user?.profilePicture.secureUrl,
        availability: formatAvailability(ins.availability),
      };
    });
  }, [availableResponse, courseMap]);
  return (
    <section className="mt-6 max-w-[1431px] px-4 py-6 sm:p-6">
      <h3 className="text-base text-[#111827] sm:text-lg">
        Currently Assigned Instructors ({assignedInstructors.length})
      </h3>

      <div className="space-y-3 border-b border-[#E5E7EB] pb-6 pt-4">
        {isAssignedInstructorsError ? (
          <p className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#991B1B]">
            {assignedInstructorsError?.data?.message === "Route not found"
              ? "The assigned-instructors endpoint could not be found on the API server."
              : assignedInstructorsError?.data?.message ||
                "Unable to load assigned instructors for this course."}
          </p>
        ) : null}

        {!isAssignedInstructorsError && assignedInstructors.length === 0 ? (
          <p className="text-sm text-[#6B7280]">No instructors assigned yet.</p>
        ) : null}

        {assignedInstructors.map((instructor) => (
          <article
            key={instructor.id}
            className="flex flex-col gap-4 rounded-lg border border-[#E5E7EB] bg-[#F0FDF4] p-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center sm:gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#D7E7F5] text-[#5A6B7C] sm:h-12 sm:w-12">
                <img src={instructor.imageUrl} alt="prfoile" />
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-base font-semibold text-[#111827]">
                    {instructor.name}
                  </h4>
                  <span className="inline-flex gap-0.5 items-center rounded-full bg-[#DCFCE7] font-medium px-[9px] py-1 text-xs text-[#166534]">
                    <Tick />
                    Assigned
                  </span>
                </div>

                <p className="break-words text-sm text-[#4B5563]">
                  {instructor.email}
                </p>

                <div className="mt-1 max-w-[431px] flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-[#6B7280]">
                  <span>Specialization: {instructor.specialization}</span>
                  <span>Assigned: {instructor.assignDate}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <button
                type="button"
                onClick={() => openModal(instructor, "remove")}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#DC2626] px-3 py-1 text-sm text-white transition hover:opacity-95"
              >
                <Minus size={10} />
                <span>Remove</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/admin/manage-user/instructor-profile/${instructor.instructorId}`,
                  )
                }
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#4B5563] px-3 py-1 text-sm text-white transition hover:opacity-95"
              >
                <Eye size={16} />
                <span>View Profile</span>
              </button>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-base text-[#111827] sm:text-lg">
            Available Instructors ({availableInstructors.length})
          </h3>

          <div className="flex flex-wrap items-center gap-2 text-sm text-[#4B5563]">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded bg-[#F3F4F6] px-3 py-1 transition hover:bg-[#E5E7EB]"
            >
              <Filter />
              <span>Filter</span>
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded bg-[#F3F4F6] px-3 py-1 transition hover:bg-[#E5E7EB]"
            >
              <SortByRating />
              <span>Sort by Rating</span>
            </button>
          </div>
        </div>

        <div className="max-h-[262px] space-y-3 overflow-y-auto pr-1">
          {availableInstructors.map((instructor) => (
            <article
              key={instructor.id}
              className="flex flex-col gap-4 rounded-lg border border-[#E5E7EB] p-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center sm:gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#D7E7F5] text-[#5A6B7C] sm:h-12 sm:w-12">
                  <img src={instructor.imageUrl} alt="profile" />
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-base font-semibold text-[#111827]">
                      {instructor.name}
                    </h4>
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#DBEAFE] px-[9px] py-1 text-xs font-medium text-[#1E40AF]">
                      Available
                    </span>
                  </div>

                  <p className="break-words text-sm text-[#4B5563]">
                    {instructor.email}
                  </p>

                  <div className="mt-1 max-w-[720px] flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#6B7280]">
                    <span>Specialization: {instructor.specialization}</span>
                  </div>

                  <p className="mt-1 text-xs text-[#6B7280]">
                    Availability: {instructor.availability}
                  </p>

                  <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-[#6B7280]">
                    <span>Preferred Courses:</span>
                    {instructor.preferredCourseTitles.length > 0 ? (
                      instructor.preferredCourseTitles.map((course) => (
                        <span
                          key={course}
                          className="rounded bg-[#F3E8FF] px-1 py-0.5 text-[#7E22CE]"
                        >
                          {course}
                        </span>
                      ))
                    ) : (
                      <span className="rounded bg-[#F3F4F6] px-1 py-0.5 text-[#6B7280]">
                        None
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                {/* <div className=" text-left">
                  <p className="text-sm font-medium text-[#CA8A04]">
                    ★ {instructor.score}
                  </p>
                  <p className="text-xs text-[#6B7280]">
                    {instructor.students}
                  </p>
                  <p className="text-xs text-[#6B7280]">{instructor.courses}</p>
                </div> */}

                <button
                  type="button"
                  onClick={() => openModal(instructor, "assign")}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#16A34A] px-4 py-2 text-sm text-white transition hover:opacity-95"
                >
                  <Plus size={16} />
                  <span>Assign to Course</span>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/admin/manage-user/instructor-profile/${instructor.userId}`,
                    )
                  }
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#4B5563] px-3 py-2 text-sm text-white transition hover:opacity-95"
                >
                  <Eye size={16} />
                  <span>View Profile</span>
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {selectedInstructor && (
        <Modal
          selectedInstructor={selectedInstructor}
          modalAction={modalAction}
          onAssign={handleAssign}
          onRemove={handleRemove}
          onClose={clearSelection}
          isAssigning={isAssigning}
          isRemoving={isRemoving}
        />
      )}
    </section>
  );
};

export default CourseInstructorsSection;
